import io from "socket.io-client";
import {
	messageReceived,
	messageRead,
	typing,
	typingStopped,
	conversationAdded,
	messageDeleted,
	SEND_MESSAGE,
	CREATE_CONVERSATION,
	CONVERSATION_CREATED,
	SEND_TYPING,
	SEND_TYPING_STOP,
	MESSAGE_READ_BY_ME,
} from "../actions/chat";
import { Dispatch, Store } from "redux";

const promisifiedEmit = (socket: SocketIOClient.Socket, event: string, args = {}) => {
	return new Promise((resolve, reject) => {
		socket.emit(event, args, res => {
			if (res.hasOwnProperty("payload")) {
				if (res.payload === null) {
					resolve(res);
				} else {
					if (res.payload.hasOwnProperty("errorMessage")) {
						//console.log({ res });
						reject(res);
					} else {
						//console.log({ res });
						resolve(res);
					}
				}
			} else {
				reject(res);
			}
		});
	});
};

export default class ChatSocketManager {
	endpoint = null;
	socket = null as SocketIOClient.Socket;
	started = false;
	dispatch = null;
	store = null as Store;
	disabled = false;

	constructor(endpoint: string) {
		this.endpoint = endpoint;
	}

	disable = () => {
		this.disabled = true;
	};

	setStore = (store: Store) => {
		this.store = store;
	};

	start(dispatch: Dispatch, token: string) {
		if (this.started || this.disabled) {
			return;
		}

		this.dispatch = dispatch;
		this.socket = io(this.endpoint, {
			autoConnect: false,
			query: {
				token,
			},
			transports: ["websocket"],
		});

		this.socket.on("disconnect", () => {
			if (this.socket) {
				this.socket.open();
				this.socket.emit("resume_session");
			}
		});

		this.socket.on("connect", () => {
			this.socket.emit("resume_session");
		});

		this.socket.on("message_received", async data => {
			const message = data.payload;
			const state = this.store.getState();
			const chatState = state.chat;

			if (!chatState.conversations.get(message.conversationId)) {
				const res = (await promisifiedEmit(this.socket, "get_conversation", {
					conversationId: message.conversationId,
				})) as any;
				const conversation = res.payload;
				dispatch(conversationAdded(conversation));
				return;
			}
			dispatch(messageReceived(data));
		});

		this.socket.on("typing", data => {
			dispatch(typing(data));
		});

		this.socket.on("noTyping", data => {
			dispatch(typingStopped(data));
		});

		this.socket.on("message_read", data => {
			dispatch(messageRead(data));
		});

		this.socket.on("message_delete", data => {
			dispatch(messageDeleted(data));
		});

		this.socket.on("user_left", data => {
			console.log({ data });
			//dispatch(messageDeleted(data));
		});

		this.socket.open();
		this.started = true;
	}

	isStarted = () => this.started;

	stop = () => {
		if (this.socket !== null) {
			this.started = false;
			this.socket.removeAllListeners();
			this.socket.close();
			this.socket = null;
		}
	};

	restart = (token: string) => {
		this.stop();
		this.start(this.dispatch, token);
	};

	createConversation = async (message: string, partners: string[]) => {
		const args = {
			eventUUID: "uuid",
			message: message,
			messageType: "message",
			partnerUUIDs: partners,
		};

		return await promisifiedEmit(this.socket, "send_message", args);
	};

	getConversation = async (partners: string[]) => {
		const args = {
			eventUUID: "uuid",
			partnerUUIDs: partners,
		};

		return await promisifiedEmit(this.socket, "get_conversation", args);
	};

	listeners = {
		[SEND_MESSAGE]: (socket: SocketIOClient.Socket, action, state) =>
			promisifiedEmit(socket, "send_message", {
				eventUUID: "uuid",
				message: action.message,
				messageType: "message",
				conversationId: action.conversationId,
			}),

		[MESSAGE_READ_BY_ME]: (socket: SocketIOClient.Socket, action, state) =>
			promisifiedEmit(socket, "message_read", {
				eventUUID: "uuid",
				messageIds: action.messageIds,
			}),

		/*
		[CREATE_CONVERSATION]: (socket, action, state) =>
			promisifiedEmit(socket, "send_message", {
				eventUUID: "uuid",
				message: action.message,
				messageType: "message",
				partnerUUIDs: action.partnerUUIDs
			}),
			*/
		[SEND_TYPING]: (socket: SocketIOClient.Socket, action, state) =>
			promisifiedEmit(socket, "typing", {
				eventUUID: "uuid",
				conversationId: action.conversationId,
				userId: action.userId,
			}),

		[SEND_TYPING_STOP]: (socket, action, state) =>
			promisifiedEmit(socket, "no_typing", {
				eventUUID: "uuid",
				conversationId: action.conversationId,
				userId: action.userId,
			}),
	};

	chatMiddleware = store => next => async action => {
		if (!this.listeners.hasOwnProperty(action.type)) {
			return next(action);
		}

		const listener = this.listeners[action.type];
		next(action);
		try {
			const res = await listener(this.socket, action, store.getState());
			if (action.type === CREATE_CONVERSATION) {
				this.socket.emit(
					"get_conversation",
					{
						conversationId: res.payload.conversationId,
					},
					res => {
						const conversation = res.payload;
						next({ type: CONVERSATION_CREATED, conversation });
					}
				);
			}

			next({ type: "chat/ACK", ack: res });
		} catch (err) {
			console.log({ err });
			next({ type: "chat/SEND_ERROR", err });
		}
	};
}
